import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { PackApiAdapter } from '../adapters/PackApiAdapter';
import { PackFilters } from '../models/Pack';
import { PackService } from '../services/PackService';

// 🎯 Hook con scroll infinito
export const usePacks = (filters: PackFilters = {}) => {
    const httpClient = createHttpClient(apiClientWH);
    const apiAdapter = new PackApiAdapter(httpClient);
    const packService = new PackService(apiAdapter);

    const {
        data,
        isLoading,
        error,
        refetch,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isPending
    } = useInfiniteQuery({
        queryKey: ['packs', filters],
        queryFn: ({ pageParam = 1 }) =>
            packService.getPacksPaginated({
                ...filters,
                page: pageParam
            }),
        getNextPageParam: (lastPage) => lastPage.nextPage,
        initialPageParam: 1,
        staleTime: 5 * 60 * 1000,
        enabled: filters.enabled !== false
    });

    // 🎯 Aplanar todas las páginas en un array único
    const packs = data?.pages.flatMap(page => page.packs) ?? [];

    // 🎯 Obtener el total del API (siempre será el mismo en todas las páginas)
    const totalFromApi = data?.pages?.[0]?.total ?? 0;

    return {
        packs,
        isLoading: isLoading || isPending,
        error,
        refetch,
        // 🎯 Nuevas props para scroll infinito
        fetchNextPage,
        hasNextPage: hasNextPage ?? false,
        isFetchingNextPage,
        // 🎯 Stats útiles
        totalPages: data?.pages.length ?? 0,
        totalPacks: packs.length,
        total: totalFromApi, // 🎯 Total real del API
    }
}

// 🎯 Hook original para compatibilidad hacia atrás (sin scroll infinito)
export const usePacksSimple = (filters: PackFilters = {}) => {
    const httpClient = createHttpClient(apiClientWH);
    const apiAdapter = new PackApiAdapter(httpClient);
    const packService = new PackService(apiAdapter);

    const {
        data: packs = [],
        isLoading,
        error,
        refetch
    } = useQuery({
        queryKey: ['packs-simple', filters],
        queryFn: () => packService.getAllPacks(filters),
        staleTime: 5 * 60 * 1000,
    });

    return {
        packs,
        isLoading,
        error,
        refetch
    }
}

// 🎯 Hook para obtener un pack específico
export const usePack = (id: number, enabled: boolean = true) => {
    const httpClient = createHttpClient(apiClientWH);
    const apiAdapter = new PackApiAdapter(httpClient);
    const packService = new PackService(apiAdapter);

    const {
        data: pack,
        isLoading,
        error,
        refetch
    } = useQuery({
        queryKey: ['pack', id],
        queryFn: () => packService.getPackById(id),
        enabled: enabled && !!id,
        staleTime: 5 * 60 * 1000,
    });

    return {
        pack,
        isLoading,
        error,
        refetch
    }
} 